// ABOUTME: Extended admin API client for workspace and agent configuration management.
// ABOUTME: Provides full CRUD operations for admin dashboard functionality.

import type {
  WorkspaceListResponse,
  WorkspaceListItem,
  WorkspaceDetail,
  WorkspaceStats,
  UsageAnalyticsResponse,
  UsageQueryParams,
  AgentConfig,
  AgentConfigDefaults,
  ConfigHistoryResponse,
  ConfigDiffResponse,
  ConfigPreviewResult,
  ImpersonationSession,
  AgentConfigUpdateRequest,
  WorkspaceUpdateRequest,
  WorkspaceCreateRequest,
  DataSource,
  DataSourceCreateRequest,
  DataSourceUpdateRequest,
  UserListResponse,
  WorkspaceUser,
  UserCreateRequest,
  UserUpdateRequest,
  SubscriptionPlanResponse,
  PlanUpdateRequest,
  EffectiveLimitsResponse,
  LimitOverrideRequest,
  ShopifyConnector,
  ShopifyInstallLinkRequest,
  ShopifyInstallLinkResponse,
  ShopifyConnectorUpdateRequest,
  ShopifyDiscoverShopResponse,
  ShopifyAutomateAppCreationResponse,
  ShopifyPartnersCredentialsStatus,
  ShopifyBrowserSessionImportResponse,
  ShopifyBrowserSessionStatus,
  ShopifyBrowserSessionKeepAliveResponse,
  ModelOverviewResponse,
  DatabricksUsageResponse,
  DatabricksDailyTrendItem,
  DatabricksUserBreakdownItem,
  DatabricksCostResponse,
} from '@/types/admin';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Token getter (set from auth context)
let getToken: (() => Promise<string>) | null = null;

export function setAdminTokenGetter(getter: () => Promise<string>) {
  getToken = getter;
}

// Base fetch wrapper
async function adminFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  let token: string | null = null;
  if (getToken) {
    try {
      token = await getToken();
    } catch (err) {
      console.error('Failed to get auth token:', err);
    }
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'unknown_error',
      message: `HTTP ${response.status}: ${response.statusText}`,
    }));
    throw new Error(errorData.message || errorData.detail || 'Request failed');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Workspace API
export const workspaceApi = {
  list: (params?: {
    page?: number;
    page_size?: number;
    subscription_tier?: string;
    search?: string;
  }): Promise<WorkspaceListResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.subscription_tier) query.set('subscription_tier', params.subscription_tier);
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return adminFetch(`/admin/workspaces${queryString ? `?${queryString}` : ''}`);
  },

  get: (workspaceId: string): Promise<WorkspaceDetail> => {
    return adminFetch<WorkspaceDetail>(`/admin/workspaces/${workspaceId}`);
  },

  getStats: (workspaceId: string): Promise<WorkspaceStats> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/stats`);
  },

  update: (workspaceId: string, data: WorkspaceUpdateRequest) => {
    return adminFetch(`/admin/workspaces/${workspaceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  getUsers: (workspaceId: string, params?: { page?: number; page_size?: number; search?: string }): Promise<UserListResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.search) query.set('search', params.search);
    const queryString = query.toString();
    return adminFetch(`/admin/workspaces/${workspaceId}/users${queryString ? `?${queryString}` : ''}`);
  },

  getUsage: (workspaceId: string, params?: UsageQueryParams): Promise<UsageAnalyticsResponse> => {
    const query = new URLSearchParams();
    if (params?.billing_period) query.set('billing_period', params.billing_period);
    if (params?.days) query.set('days', String(params.days));
    if (params?.feature && params.feature.length > 0) {
      params.feature.forEach(f => query.append('feature', f));
    }
    if (params?.model) query.set('model', params.model);
    const queryString = query.toString();
    return adminFetch(`/admin/workspaces/${workspaceId}/usage${queryString ? `?${queryString}` : ''}`);
  },

  create: (data: WorkspaceCreateRequest): Promise<WorkspaceListItem> => {
    return adminFetch('/admin/workspaces', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  delete: (workspaceId: string): Promise<void> => {
    return adminFetch(`/admin/workspaces/${workspaceId}`, {
      method: 'DELETE',
    });
  },

  retryProvisioning: (workspaceId: string): Promise<{ success: boolean; message: string }> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/retry-provisioning`, {
      method: 'POST',
    });
  },

  setAnthropicApiKey: (workspaceId: string, apiKey: string): Promise<void> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/anthropic-api-key`, {
      method: 'PUT',
      body: JSON.stringify({ api_key: apiKey }),
    });
  },

  getDatabricksUsage: (workspaceId: string, params?: { days?: number }): Promise<DatabricksUsageResponse> => {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const queryString = query.toString();
    return adminFetch(`/admin/workspaces/${workspaceId}/databricks-usage${queryString ? `?${queryString}` : ''}`);
  },

  getDatabricksDailyTrend: (workspaceId: string, params?: { days?: number }): Promise<DatabricksDailyTrendItem[]> => {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const queryString = query.toString();
    return adminFetch(`/admin/workspaces/${workspaceId}/databricks-usage/daily${queryString ? `?${queryString}` : ''}`);
  },

  getDatabricksUserBreakdown: (workspaceId: string, params?: { days?: number }): Promise<DatabricksUserBreakdownItem[]> => {
    const query = new URLSearchParams();
    if (params?.days) query.set('days', String(params.days));
    const queryString = query.toString();
    return adminFetch(`/admin/workspaces/${workspaceId}/databricks-usage/users${queryString ? `?${queryString}` : ''}`);
  },

  enrichDatabricksUsage: (workspaceId: string): Promise<{ enriched_count: number; skipped_count: number; error_count: number }> => {
    return adminFetch(`/admin/databricks/enrich-usage?workspace_id=${workspaceId}`, {
      method: 'POST',
    });
  },

  getDatabricksCost: (warehouseId: string, params?: { days?: number; workspaceId?: string }): Promise<DatabricksCostResponse> => {
    const query = new URLSearchParams();
    query.set('warehouse_id', warehouseId);
    if (params?.workspaceId) query.set('workspace_id', params.workspaceId);
    if (params?.days) query.set('days', String(params.days));
    return adminFetch(`/admin/databricks/cost?${query.toString()}`);
  },
};

// Schema sync response type
interface SchemaSyncResponse {
  tables_discovered: number;
  schemas_discovered: string[];
  last_schema_sync_at: string;
}

// Data Source API
export const dataSourceApi = {
  add: (workspaceId: string, data: DataSourceCreateRequest): Promise<DataSource> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/data-sources`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: (
    workspaceId: string,
    dataSourceId: string,
    data: DataSourceUpdateRequest
  ): Promise<DataSource> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/data-sources/${dataSourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  remove: (workspaceId: string, dataSourceId: string): Promise<void> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/data-sources/${dataSourceId}`, {
      method: 'DELETE',
    });
  },

  syncSchema: (workspaceId: string, dataSourceId: string): Promise<SchemaSyncResponse> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/data-sources/${dataSourceId}/sync-schema`, {
      method: 'POST',
    });
  },
};

// Shopify Connector API
export const shopifyConnectorApi = {
  get: (workspaceId: string): Promise<ShopifyConnector> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector`),

  generateInstallLink: (workspaceId: string, data: ShopifyInstallLinkRequest): Promise<ShopifyInstallLinkResponse> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector/install-link`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (workspaceId: string, data: ShopifyConnectorUpdateRequest): Promise<ShopifyConnector> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  remove: (workspaceId: string): Promise<void> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector`, {
      method: 'DELETE',
    }),

  triggerRun: (workspaceId: string): Promise<{ success: boolean; message: string }> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector/run`, {
      method: 'POST',
    }),

  triggerRunLocal: (workspaceId: string): Promise<{ success: boolean; message: string }> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector/run-local`, {
      method: 'POST',
    }),

  register: (workspaceId: string, data: ShopifyInstallLinkRequest): Promise<ShopifyConnector> =>
    adminFetch(`/admin/workspaces/${workspaceId}/shopify-connector/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  discoverShop: (url: string): Promise<ShopifyDiscoverShopResponse> =>
    adminFetch('/admin/shopify/discover-shop', {
      method: 'POST',
      body: JSON.stringify({ url }),
    }),

  automateAppCreation: (
    shopUrl: string,
    scopes: string[],
    workspaceId: string,
    resources?: string[],
    scheduleFrequencyMinutes?: number,
  ): Promise<ShopifyAutomateAppCreationResponse> =>
    adminFetch('/admin/shopify/automate-app-creation', {
      method: 'POST',
      body: JSON.stringify({
        shop_url: shopUrl,
        scopes,
        workspace_id: workspaceId,
        ...(resources ? { resources } : {}),
        ...(scheduleFrequencyMinutes ? { schedule_frequency_minutes: scheduleFrequencyMinutes } : {}),
      }),
    }),

  // Note: No AbortController — if the admin closes the modal mid-automation,
  // the backend Playwright session continues to completion. This is intentional:
  // the browser automation should finish creating the app rather than leaving
  // a half-created app in the Shopify dashboard.
  automateAppCreationStream: async (
    shopUrl: string,
    scopes: string[],
    workspaceId: string,
    onStep: (step: { step: string; success: boolean; detail: string }) => void,
    resources?: string[],
    scheduleFrequencyMinutes?: number,
  ): Promise<ShopifyAutomateAppCreationResponse> => {
    const url = `${API_BASE_URL}/admin/shopify/automate-app-creation-stream`;

    let token: string | null = null;
    if (getToken) {
      try {
        token = await getToken();
      } catch (err) {
        console.error('Failed to get auth token:', err);
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        shop_url: shopUrl,
        scopes,
        workspace_id: workspaceId,
        ...(resources ? { resources } : {}),
        ...(scheduleFrequencyMinutes ? { schedule_frequency_minutes: scheduleFrequencyMinutes } : {}),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw new Error(errorData.message || errorData.detail || 'Request failed');
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body is not readable');

    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: ShopifyAutomateAppCreationResponse | null = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const events = buffer.split('\n\n');
      // Keep the last incomplete chunk in the buffer
      buffer = events.pop() || '';

      for (const eventBlock of events) {
        if (!eventBlock.trim()) continue;

        let eventType = '';
        let eventData = '';

        for (const line of eventBlock.split('\n')) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }

        if (!eventType || !eventData) continue;

        try {
          const parsed = JSON.parse(eventData);

          if (eventType === 'step') {
            onStep(parsed);
          } else if (eventType === 'complete') {
            finalResult = {
              success: true,
              client_id: parsed.client_id || '',
              client_secret: parsed.client_secret || '',
              app_url: parsed.app_url || '',
              our_install_url: parsed.our_install_url || '',
              error: '',
              error_step: '',
              steps: [],
            };
          } else if (eventType === 'error') {
            finalResult = {
              success: false,
              client_id: '',
              client_secret: '',
              app_url: '',
              our_install_url: '',
              error: parsed.error || 'Unknown error',
              error_step: parsed.error_step || '',
              steps: [],
            };
          }
        } catch {
          console.warn('Failed to parse SSE event data:', eventData);
        }
      }
    }

    if (!finalResult) {
      throw new Error('Stream ended without a complete or error event');
    }

    return finalResult;
  },

  storePartnersCredentials: (email: string, password: string, devOrgId: string): Promise<void> =>
    adminFetch('/admin/shopify/store-partners-credentials', {
      method: 'POST',
      body: JSON.stringify({ email, password, dev_org_id: devOrgId }),
    }),

  getPartnersCredentialsStatus: (): Promise<ShopifyPartnersCredentialsStatus> =>
    adminFetch('/admin/shopify/partners-credentials-status'),

  importBrowserSession: (cookies: unknown[]): Promise<ShopifyBrowserSessionImportResponse> =>
    adminFetch('/admin/shopify/import-browser-session', {
      method: 'POST',
      body: JSON.stringify({ cookies }),
    }),

  getSessionStatus: (): Promise<ShopifyBrowserSessionStatus> =>
    adminFetch('/admin/shopify/session-status'),

  keepSessionAlive: (): Promise<ShopifyBrowserSessionKeepAliveResponse> =>
    adminFetch('/admin/shopify/keep-session-alive', {
      method: 'POST',
    }),
};

// Agent Configuration API
export const agentConfigApi = {
  // Get default configuration values (not workspace-specific)
  getDefaults: (): Promise<AgentConfigDefaults> => {
    return adminFetch('/admin/workspaces/agent-config/defaults');
  },

  get: (workspaceId: string): Promise<AgentConfig> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config`);
  },

  update: (workspaceId: string, data: AgentConfigUpdateRequest): Promise<AgentConfig> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getHistory: (workspaceId: string, limit: number = 20): Promise<ConfigHistoryResponse> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config/history?limit=${limit}`);
  },

  getVersion: (workspaceId: string, version: number): Promise<AgentConfig> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config/version/${version}`);
  },

  rollback: (workspaceId: string, targetVersion: number): Promise<AgentConfig> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config/rollback`, {
      method: 'POST',
      body: JSON.stringify({ target_version: targetVersion }),
    });
  },

  reset: (workspaceId: string): Promise<AgentConfig> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config`, {
      method: 'DELETE',
    });
  },

  preview: (workspaceId: string, data: AgentConfigUpdateRequest): Promise<ConfigPreviewResult> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/agent-config/preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getDiff: (workspaceId: string, version1: number, version2: number): Promise<ConfigDiffResponse> => {
    return adminFetch(
      `/admin/workspaces/${workspaceId}/agent-config/diff?version1=${version1}&version2=${version2}`
    );
  },

  getModelOverview: (workspaceId: string): Promise<ModelOverviewResponse> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/model-overview`);
  },
};

// User Management API
export const userApi = {
  listAll: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    exclude_workspace_id?: string;
  }): Promise<UserListResponse> => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.page_size) query.set('page_size', String(params.page_size));
    if (params?.search) query.set('search', params.search);
    if (params?.exclude_workspace_id) query.set('exclude_workspace_id', params.exclude_workspace_id);
    const queryString = query.toString();
    return adminFetch(`/admin/users${queryString ? `?${queryString}` : ''}`);
  },

  add: (workspaceId: string, data: UserCreateRequest): Promise<WorkspaceUser> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateRole: (workspaceId: string, userId: string, data: UserUpdateRequest): Promise<WorkspaceUser> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  remove: (workspaceId: string, userId: string): Promise<void> => {
    return adminFetch(`/admin/workspaces/${workspaceId}/users/${userId}`, {
      method: 'DELETE',
    });
  },
};

// Impersonation API
export const impersonationApi = {
  create: (targetUserId: string): Promise<ImpersonationSession> => {
    return adminFetch('/admin/impersonate', {
      method: 'POST',
      body: JSON.stringify({ target_user_id: targetUserId }),
    });
  },
};

// Plan Management API
export const planApi = {
  list: (): Promise<SubscriptionPlanResponse[]> =>
    adminFetch('/admin/plans'),

  get: (name: string): Promise<SubscriptionPlanResponse> =>
    adminFetch(`/admin/plans/${name}`),

  update: (name: string, data: PlanUpdateRequest): Promise<SubscriptionPlanResponse> =>
    adminFetch(`/admin/plans/${name}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// Workspace Limit Override API
export const limitOverrideApi = {
  getEffective: (workspaceId: string): Promise<EffectiveLimitsResponse> =>
    adminFetch(`/admin/workspaces/${workspaceId}/effective-limits`),

  update: (workspaceId: string, data: LimitOverrideRequest): Promise<EffectiveLimitsResponse> =>
    adminFetch(`/admin/workspaces/${workspaceId}/limit-overrides`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  updateFeatures: (workspaceId: string, data: Record<string, boolean>): Promise<EffectiveLimitsResponse> =>
    adminFetch(`/admin/workspaces/${workspaceId}/feature-overrides`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
};

// Export combined admin API
export const adminApi = {
  workspaces: workspaceApi,
  dataSources: dataSourceApi,
  shopifyConnector: shopifyConnectorApi,
  users: userApi,
  agentConfig: agentConfigApi,
  impersonation: impersonationApi,
  plans: planApi,
  limitOverrides: limitOverrideApi,
};
